// Single source of truth for API paths. Mirrors awiday-web-api/routes/api.php.
// Add new entries here as features come online; never hardcode paths in components.
export const endpoints = {
  // Public
  loginOtp: '/login-otp',
  login: '/login',
  countries: '/countries',

  // Authenticated (any role)
  profileList: '/profile-list',
  logout: '/logout',
  updateFirebaseToken: '/update-firebase-token',
  // Firebase web config, served by the API so the browser console and the
  // blade console share one source of truth (and nothing is baked into the
  // bundle at build time).
  firebaseConfig: '/firebase-config',
  deleteOtp: '/delete-otp',
  delete: '/delete',
  memberships: '/memberships',
  paymentRedirect: '/payment-redirect',
  // Shared across all roles — not under a role base, so not in `rpath`.
  bannerClick: (bannerId: number) => `/banners/${bannerId}/click`,

  // Admin
  // Dashboard paths now live in `rpath` below — every role tree serves them.
  adminDashboard: '/admin/dashboard',
  adminUpdateProfile: '/admin/update-profile',
  adminBranches: '/admin/branches',
  adminBatches: '/admin/batches',
  adminStudents: '/admin/students',
  adminTrainers: '/admin/trainers',
  adminManagers: '/admin/managers',
  adminLeads: '/admin/leads',
  adminLeadStatus: '/admin/lead-status',
  adminAssignable: '/admin/assignable',
  adminFeeDetails: '/admin/fee-details',
  adminOtherFees: '/admin/fee-details/other-fees',
  adminSalary: '/admin/salary',
  adminClassLinks: '/admin/class-links',
  adminCalendarDay: (date: string) => `/admin/calendar/${date}`,
  adminCalendarRange: (start: string, end: string) => `/admin/calendar/range/${start}/${end}`,
  adminMembershipStatus: '/admin/membership/status',
  adminNotifications: '/admin/notifications',
  adminNotification: (id: string) => `/admin/notifications/${id}`,
  adminNotificationMarkRead: (id: string) => `/admin/notifications/mark-as-read/${id}`,
  adminNotificationsMarkReadBulk: '/admin/notifications/mark-as-read-bulk',
  adminNotificationsDeleteBulk: '/admin/notifications/delete-bulk',
  // Every student payment across the four money-in tables (admin-only)
  adminPayments: '/admin/payments',
  // Filter options: manual modes + the "Partapp" gateway group (academy-aware)
  paymentMethodsFilter: '/payment-methods/filter',
  // Cashfree settlements (admin-only; academy resolved server-side)
  adminSettlements: '/admin/settlements',
  adminSettlement: (id: number | string) => `/admin/settlements/${id}`,
  adminSettlementSyncTransactions: (id: number | string) =>
    `/admin/settlements/${id}/sync-transactions`,
  adminSettlementsExport: '/admin/settlements/export',
} as const;

// Scope-aware resource path builders. These resolve to the admin tree
// (/admin/...) or the manager tree (/managers/{managerId}/...) based on the active
// profile. Use these (not the flat admin* constants) in any api/*.ts module
// that must work for both admin and manager. The admin* constants above are
// kept for admin-only callers (membership, managers CRUD, etc.).
//
// Admin vs manager trees diverge structurally for fees/salary/calendar — those
// are mapped per-module against awiday-web-api/routes/api.managers.php, not
// here. Only the resources whose path shape is `${base}/<resource>` live here.
import { activeScope } from './scope';

export const rpath = {
  dashboard: () => `${activeScope().base}/dashboard`,
  // KPI tiles. All three trees expose this, each scoped server-side: an admin
  // gets the academy, a manager their branches, a trainer their batches. The
  // payload shape differs per role — see DashboardKpis in api/dashboard.ts.
  dashboardKpis: () => `${activeScope().base}/dashboard-kpis`,
  // Manager-only: per-branch breakdown across their assigned branches.
  branchesSummary: () => `${activeScope().base}/branches-summary`,
  // Dashboard lists. Also scoped per tree — a trainer's copies cover only the
  // batches they take, a manager's only their branches.
  recentFeePayments: () => `${activeScope().base}/recent-fee-payments`,
  recentlyConductedClasses: () => `${activeScope().base}/recently-conducted-classes`,
  // Today's (or any day's) classes, mirroring the mobile Today's Classes screen.
  dailyClasses: (date: string) => `${activeScope().base}/batches/daily-classes/${date}`,
  students: () => `${activeScope().base}/students`,
  studentsExport: () => `${activeScope().base}/students/export`,
  batches: () => `${activeScope().base}/batches`,
  trainers: () => `${activeScope().base}/trainers`,
  leads: () => `${activeScope().base}/leads`,
  leadStatus: () => `${activeScope().base}/lead-status`,

  // New leads CRM surface. Lives under its own `crm` prefix server-side
  // because the legacy `leads` group registers a `{lead}` wildcard that would
  // otherwise swallow static segments like `search`. The legacy lead endpoints
  // above are unchanged and still serve the shipped mobile apps.
  leadPipeline: () => `${activeScope().base}/crm/leads/pipeline`,
  leadSearch: () => `${activeScope().base}/crm/leads/search`,
  leadAnalytics: () => `${activeScope().base}/crm/leads/analytics`,
  leadExport: () => `${activeScope().base}/crm/leads/export`,
  leadBulk: () => `${activeScope().base}/crm/leads/bulk`,
  leadActivities: (leadId: number | string) =>
    `${activeScope().base}/crm/leads/${leadId}/activities`,
  leadWorkQueue: () => `${activeScope().base}/crm/follow-ups/work-queue`,
  leadFollowUpComplete: (followUpId: number | string) =>
    `${activeScope().base}/crm/follow-ups/${followUpId}/complete`,
  leadSources: () => `${activeScope().base}/crm/lead-sources`,
  // Cashfree-paid invoices with their settlement status. Exists in all three
  // trees: an admin sees the academy, a manager their branches, a trainer their
  // batches. The settlement (payout) endpoints above stay admin-only.
  settlementInvoices: () => `${activeScope().base}/settlement-invoices`,
  branches: () => `${activeScope().base}/branches`,
  assignable: () => `${activeScope().base}/assignable`,
  notifications: () => `${activeScope().base}/notifications`,
  // Manager fee-details/salary trees mirror the admin sub-structure exactly
  // (batch-fee-invoices, admission-fee-invoices, additional-fee-invoices,
  // other-payments, other-fees, advance-payment) — only the base differs.
  feeDetails: () => `${activeScope().base}/fee-details`,
  otherFees: () => `${activeScope().base}/fee-details/other-fees`,
  salary: () => `${activeScope().base}/salary`,
  calendar: (date: string) => `${activeScope().base}/calendar/${date}`,
  updateProfile: () => `${activeScope().base}/update-profile`,
  oneTimeClasses: () => `${activeScope().base}/one-time-classes`,
} as const;
